import { useEffect, useState, useRef } from 'react';
import { useAppDispatch, Tenant, useAppForeground } from '@/hooks';
import { getAuthUser, getPublicUser } from '@/state/slices/authSlice';
import { AuthService } from '@/services';
import { User } from '@supabase/supabase-js';

interface UseAuthInitOptions {
  tenant: Tenant;
}

export const useAuthInit = ({ tenant }: UseAuthInitOptions) => {
  const dispatch = useAppDispatch();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const hasAuthStarted = useRef(false);
  const { isForeground } = useAppForeground();

  useEffect(() => {
    if (hasAuthStarted.current || !isForeground) return;
    hasAuthStarted.current = true;

    // Initialize auth with session-based check
    dispatch(getAuthUser()).then((result) => {
      // If we have auth user and tenant is Zeus or Buyer, fetch public user
      const authUser = result.payload as User | null;
      if (authUser && (tenant === Tenant.Zeus || tenant === Tenant.Buyer) && authUser.id) {
        dispatch(getPublicUser(authUser.id));
      }
      setIsAuthReady(true);
    }).catch(() => {
      setIsAuthReady(true);
    });

    // Listen for auth state changes
    const { data: { subscription } } = AuthService.onAuthStateChange((user) => {
      if (user && isForeground) {
        dispatch(getAuthUser()).then((result) => {
          // If we have auth user and tenant is Zeus or Buyer, fetch public user
          const authUser = result.payload as User | null;
          if (authUser && (tenant === Tenant.Zeus || tenant === Tenant.Buyer) && authUser.id) {
            dispatch(getPublicUser(authUser.id));
          }
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [dispatch, tenant, isForeground]);

  return { isAuthReady };
}; 